import {motion} from "framer-motion"
import Link from "next/link"
import DashboardIcon from "@material-ui/icons/Dashboard"
import PeopleAltIcon from "@material-ui/icons/PeopleAlt"
import HomeWorkIcon from "@material-ui/icons/HomeWork"
import ListAltIcon from "@material-ui/icons/ListAlt"
import PublicIcon from "@material-ui/icons/Public"
import SmsIcon from "@material-ui/icons/Sms"
import PhoneIcon from "@material-ui/icons/PhoneCallback"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import WidgetsIcon from "@material-ui/icons/Widgets"
import AppsIcon from "@material-ui/icons/Apps"
import LeadPagesIcon from "@material-ui/icons/Web"
import {useMemo} from "react"
import {useLocalStorage} from "../lib/hooks/useLocalStorage"
import PlaybooksIcon from "@material-ui/icons/Book"
import CallsterIcon from "@material-ui/icons/Call"

const navLinks = [
  {
    name: "dashboard",
    link: "/app",
    icon: () => <DashboardIcon></DashboardIcon>,
  },
  {
    name: "leads",
    link: "/leads",
    icon: () => <PeopleAltIcon></PeopleAltIcon>,
  },
  {
    name: "listings",
    link: "/listing",
    icon: () => <HomeWorkIcon></HomeWorkIcon>,
  },
]

const toolLinks = [
  {
    name: "ads",
    link: "/ads",
    icon: () => <ListAltIcon/>,
  },
  {
    name: "web",
    link: "/website",
    icon: () => <PublicIcon/>,
  },
  {
    name: "sms",
    link: "/sms",
    icon: () => <SmsIcon/>,
  },
  {
    name: "phone",
    link: "/phone",
    icon: () => <PhoneIcon/>,
  },
  {
    name: "pages",
    link: "/leadPages",
    icon: () => <div className="relative">
      <LeadPagesIcon/>
      <div className="absolute left-0 ml-3 bottom-0 mb-5 text-2xs text-purple-500 font-bold">
          {"beta"}
      </div>
    </div>,
  },
  {
    name: "playbooks",
    link: "/playbooks",
    icon: () => <PlaybooksIcon/>
  },
]

const appLinks = [
  {
    name: "Fizbonanza", 
    link: "https://fizbonanza.com", 
    icon: () => <img src="/apps/fizbonanza.png" height={25} width={25}/>
  },
  {
    name: "Listing Cake", 
    link: "https://listingcake.com", 
    icon: () => <img className="bg-gray-900" src="/apps/listing-cake.png" alt="listing cake logo" height={25} width={25}/>
  },
  {
    name: "Nerdsheets", 
    link: "http://www.nerdsheets.com/", 
    icon: () => <img src="/apps/nerdsheets.ico" height={25} width={25}/>
  },
  {
    name: "Callster",
    link: "http://www.callster.io/", 
    icon: () => <div
      style={{
        width: "25px",
        height: "25px",
        color: "#00539B"
      }}
    >
      <CallsterIcon/>
    </div>
  },
  {
    name: "Splitcrunch", 
    link: "http://www.splitcrunch.com/", 
    icon: () => <img src="/apps/split-crunch.png" height={25} width={25}/>
  },
  {
    name: "Photobrand", 
    link: "https://www.photobrand.io/", 
    icon: () => <img src="/apps/free-photo-branding.png" height={25} width={25}/>
  },
  {
    name: "BP CMA", 
    link: "https://www.bulletproofcma.com/", 
    icon: () => <img src="/apps/bulletproof-cma.png" title="Bulletproof CMA" alt="Bulletproof CMA Icon" height={25} width={25}/>
  },
  {
    name: "Hatcher", 
    link: "https://www.heyhatcher.com/", 
    icon: () => <div
      className="relative rounded-full text-center font-bold"
      style={{
        height: "25px", 
        width: "25px",
        backgroundColor: "rgba(221, 107, 32, 1)"
      }}
    >
      <div 
        className="absolute"
        style={{top: "1.5px", left: "8.5px"}}
      >{"h"}</div>
      
    </div>
  },
]

export default function AppNavMotion(): JSX.Element {
  const [collapsed, setCollapsed] = useLocalStorage("nav-collapsed", false)
  const [showTools, setShowTools] = useLocalStorage("show-tools", false)
  const [showApps, setShowApps] = useLocalStorage("show-apps", false)

  const path = useMemo(() => {
    if (typeof window === "undefined") {
      return ""
    }
    return window.location.pathname
  }, [])

  return <div className={"flex flex-row items-center" + " sm:flex-col sm:ml-4"}>
    <motion.button
      type="button"
      layout="position"
      onClick={() => setCollapsed(!collapsed)}
      className={
        "hidden sm:inline text-sm" +
        " text-ppt-blue hover:text-ppt-success" +
        (collapsed ? "" : " self-end")
      }
    >
      {collapsed ? (
        <ChevronRightIcon/>
      ) : (
        <ChevronLeftIcon/>
      )}
    </motion.button>
    <motion.nav
      layout
      className={
        "flex items-center" +
        " sm:flex-col sm:items-start" +
        " bg-ppt-blue-800 z-20" +
        " w-1/2 sm:w-full" +
        " box-shadow-subtle"
      }
    >
      {navLinks.map(({name, link, icon}) => (
        <Link href={link} key={link}>
          <motion.a
            layout="position"
            className={
              "flex items-center justify-center cursor-pointer" +
              " w-full px-2 py-1" +
              " border-t-4 border-transparent hover:border-ppt-blue" +
              " sm:border-t-0 sm:border-r-4 sm:justify-start" +
              (path.includes(link)
                ? " bg-ppt-blue-900 text-ppt-lighttext border-ppt-red"
                : "")
            }
          >
            <span className={"text-gray-100 tooltip"} title={name}>
              {icon()}
              {collapsed ? (
                <span
                  className={
                    "tooltip-text z-30 bg-ppt-blue-900 rounded" +
                    " text-ppt-lighttext ml-4 font-display text-xs opacity-75"
                  }
                >
                  {name}
                </span>
              ) : null}
            </span>
            {collapsed ? null : (
              <span className={"font-display sm:ml-1" + " hidden sm:inline"}>
                {name}
              </span>
            )}
          </motion.a>
        </Link>
      ))}

    {/* tools dropdown */}
    
    <motion.button
          layout="position"
          onClick={() => setShowTools(!showTools)}
          className={
            "flex items-center justify-center cursor-pointer" +
            " w-full px-2 py-1" +
            " border-t-4 border-transparent hover:border-ppt-blue" +
            " sm:border-t-0 sm:border-r-4 sm:justify-start"
          }
      >
          <WidgetsIcon/>
          {collapsed ? <></> : <>
            <span className={"font-display sm:ml-1" + " hidden sm:inline"}>
              {"tools"}
            </span>
            {showTools ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </>}
    </motion.button>

    <motion.nav className="w-full">
      {showTools
        ? toolLinks.map(({ name, link, icon }) => (
            <Link href={link} key={link}>
              <motion.a
                layout="position"
                className={
                  "text-gray-800 text-sm bg-ppt-gray-200 flex items-center justify-center cursor-pointer" +
                  " w-full px-2 py-1" +
                  " border-t-4 border-transparent hover:border-ppt-blue" +
                  " sm:border-t-0 sm:border-r-4 sm:justify-start" +
                  (path === link || path.startsWith(link)
                    ? " bg-ppt-blue-900  border-ppt-red"
                    : "")
                }
              >
                <span className="tooltip" title={name}>
                  {icon()}
                  {collapsed ? (
                    <span
                      className={
                        "tooltip-text z-30 bg-ppt-blue-900 rounded" +
                        " text-ppt-lighttext ml-4 font-display text-xs opacity-75"
                      }
                    >
                      {name}
                    </span>
                  ) : null}
                </span>
                {collapsed ? null : (
                  <span
                    className={"font-display sm:ml-1" + " hidden sm:inline"}
                  >
                    {name}
                  </span>
                )}
              </motion.a>
            </Link>
          ))
        : null}
      </motion.nav>

      {/* apps dropdown */}

      <motion.button
          layout="position"
          onClick={() => setShowApps(!showApps)}
          className={
            "flex items-center justify-center cursor-pointer" +
            " w-full px-2 py-1" +
            " border-t-4 border-transparent hover:border-ppt-blue" +
            " sm:border-t-0 sm:border-r-4 sm:justify-start"
          }
        >
          <AppsIcon></AppsIcon>
          {collapsed ? <></> : <>
            <span className={"font-display sm:ml-1" + " hidden sm:inline"}>
              {"apps"}
            </span>
            {showApps ? (
              <ExpandLessIcon></ExpandLessIcon>
            ) : (
              <ExpandMoreIcon></ExpandMoreIcon>
            )}
          </>}
        </motion.button>

        <motion.nav className="w-full">
          {showApps
            ? appLinks.map(({ name, link, icon }) => (
                <a href={link} key={link} target="_blank" rel="noopener">
                  <motion.a
                    layout="position"
                    className={
                      "text-gray-800 text-sm bg-ppt-gray-200 flex items-center justify-center cursor-pointer" +
                      " w-full px-1 py-1" +
                      " border-t-4 border-transparent hover:border-ppt-blue" +
                      " sm:border-t-0 sm:border-r-4 sm:justify-start"
                    }
                  >
                    <span className="tooltip" title={name}>
                      {icon()}
                      {collapsed ? (
                        <span
                          className={
                            "tooltip-text z-30 bg-ppt-blue-900 rounded" +
                            " text-ppt-lighttext ml-4 font-display text-sm opacity-75"
                          }
                        >
                          {name}
                        </span>
                      ) : null}
                    </span>
                    {collapsed ? null : (
                      <span
                        className={"font-display sm:ml-1" + " hidden sm:inline"}
                      >
                        {name}
                      </span>
                    )}
                  </motion.a>
                </a>
              ))
            : null}
      </motion.nav>
    </motion.nav>
  </div>
}